<script setup>
    // Component
    import MainHeader from '@/components/HeaderComponent.vue';
    import detail from '@/components/modal/CompanyManagementDetailComponent.vue';
    import userSelect from '@/components/modal/UserSelectComponent.vue';
    import deleteModal from '@/components/modal/DeleteConfirmComponent.vue';
    import { ref, onMounted, inject, getCurrentInstance } from 'vue';
    import { CompanyManagementListRequest } from '@/models/companyManagement/companyManagementModel';
    import { useCompanyManagementListStore } from '@/stores/companyManagement/companyManagementListStore';
    import Utils from '@/commonProject/helpers/utils.js';
    import Pagination from '@/components/PaginationComponent.vue';
    import i18n from '@/common/i18n';

    const app = getCurrentInstance();
    const API_URL = app.appContext.config.globalProperties.$constProperties.BASE_API_URL_S;

    // 管理者の管理ストア
    const companyManagementListStore = useCompanyManagementListStore();
    // 管理者一覧検索項目
    const companyManagementListRequest = ref({});
    // 管理者一覧検索済み項目
    const companyManagementListRequestDone = ref({});
    // メッセージダイアログ取得
    const { messageDialog } = inject('messageDialogKey');
    // ページングパーツ
    const currentPage = ref(1);
    const totalPages = ref(0);

    // 削除用PK保持
    const deletedPk = ref('');
    /**
     * 初期処理
     */
    onMounted(async () => {
        // Storeを初期化する。
        companyManagementListStore.reset();

        // 管理者一覧検索項目の初期化
        companyManagementListRequest.value = new CompanyManagementListRequest();

        // 管理者一覧検索済み項目の初期化
        companyManagementListRequestDone.value = new CompanyManagementListRequest();

        // 管理者一覧取得
        await getCompanyManagementList();
    });

    /**
     * 管理者一覧取得処理
     */
    async function getCompanyManagementList() {
        const maxPage = 50;
        const requestBody = {
            PageCond: {
                RowPosition: maxPage * (currentPage.value - 1) + 1,
                RowAmount: maxPage,
                RowCount: true,
            },
        };

        //MngServiceAdminFlagがtrueの場合、リクエストボディにMngServiceAdminFlagを追加する処理
        if (companyManagementListRequest.value.MngServiceAdminFlag) {
            if (!requestBody['Cond']) {
                requestBody['Cond'] = {};
            }
            requestBody['Cond']['MngServiceAdminFlag'] = true;
        }
        //MngSystemAdminFlagがtrueの場合、リクエストボディにMngSystemAdminFlagを追加する処理
        if (companyManagementListRequest.value.MngSystemAdminFlag) {
            if (!requestBody['Cond']) {
                requestBody['Cond'] = {};
            }
            requestBody['Cond']['MngSystemAdminFlag'] = true;
        }
        //MngHealthStaffFlagがtrueの場合、リクエストボディにMngHealthStaffFlagを追加する処理
        if (companyManagementListRequest.value.MngHealthStaffFlag) {
            if (!requestBody['Cond']) {
                requestBody['Cond'] = {};
            }
            requestBody['Cond']['MngHealthStaffFlag'] = true;
        }
        //MngIncidentReportFlagがtrueの場合、リクエストボディにMngIncidentReportFlagを追加する処理
        if (companyManagementListRequest.value.MngIncidentReportFlag) {
            if (!requestBody['Cond']) {
                requestBody['Cond'] = {};
            }
            requestBody['Cond']['MngIncidentReportFlag'] = true;
        }
        //MngCrisisReportFlagがtrueの場合、リクエストボディにMngCrisisReportFlagを追加する処理
        if (companyManagementListRequest.value.MngCrisisReportFlag) {
            if (!requestBody['Cond']) {
                requestBody['Cond'] = {};
            }
            requestBody['Cond']['MngCrisisReportFlag'] = true;
        }

        const response = await companyManagementListStore.postMngLstAction(API_URL, requestBody);

        if (response.success) {
            totalPages.value = response.RowResult;
            handleOnloadPagination(totalPages.value);
            companyManagementListRequestDone.value = companyManagementListRequest.value;
        } else {
            messageDialog('error', response.errorMessage);
            if (response.redirect) {
                location.href = response.redirectUrl;
            }
            return;
        }
    }

    // 管理者詳細モーダル
    const detailDispFlg = ref('');
    const detailComponent = ref();
    /**
     * 管理者詳細モーダル
     * @param UserPk
     */
    function openDetail(UserPk) {
        detailComponent.value.setInfo(UserPk);
        detailDispFlg.value = 'block';
    }

    function closeDetail() {
        detailDispFlg.value = '';
    }
    async function closeDetailAndUpdateList() {
        detailDispFlg.value = '';

        // 1ページ目に座標を戻す。
        currentPage.value = 1;

        // 一覧を取得しなおす。
        await getCompanyManagementList();
    }

    /**
     * ユーザー選択モーダルオープン
     */
    function companyManagementUserSelectOpen() {
        userSelectComponent.value.openModal(API_URL, '1');
        detailDispFlg.value = '';
        userSelectDispFlg.value = 'block';
    }

    // ユーザー選択モーダル
    const userSelectDispFlg = ref('');
    const userSelectComponent = ref();
    /**
     * ユーザー選択モーダルクローズ
     */
    function userSelectClose() {
        detailDispFlg.value = '';
        userSelectDispFlg.value = '';
    }
    /**
     * ユーザー選択の選択処理
     * @param UserPk
     * @param UserNm
     */
    function userSelected(UserPk, UserNm) {
        // ユーザ選択
        detailComponent.value.selectModal(UserPk, UserNm);
        detailDispFlg.value = 'block';
        userSelectDispFlg.value = '';
    }

    /**
     * ページ操作
     * @param newPage
     */
    async function handlePageChange(newPage) {
        currentPage.value = newPage;

        // ページング実行により、一覧取得APIを再実行する。
        await getCompanyManagementList();
    }
    const pagination = ref();
    /**
     * ページングの初期処理を実行する。
     * 取得したAPIのデータ数を引き渡す。
     */
    function handleOnloadPagination(RowResult) {
        pagination.value.onload(RowResult);
    }

    /**
     * 削除処理
     * @param UserPk PK
     */
    async function deleted(UserPk) {
        const requestBody = {
            UserPks: [UserPk],
        };
        const response = await companyManagementListStore.postMngDelAction(API_URL, requestBody);
        if (!response.success) {
            messageDialog('error', response.errorMessage);
            if (response.redirect) {
                location.href = response.redirectUrl;
            }
            return;
        }
    }

    /**
     * 削除確認モーダル
     * @param UserPk
     */
    const deletedDispFlg = ref('');
    const deletedComponent = ref();
    async function deletedModal(UserPk) {
        if (companyManagementListStore.mngList.length !== 1) {
            if (!Utils.isNullEmpty(UserPk)) {
                deletedPk.value = UserPk;
                deletedDispFlg.value = 'block';
            }
        } else {
            messageDialog('error', i18n.global.t('CompanyManagementList.message.message1'));
            return;
        }
    }

    /**
     * 削除確認モーダル「いいえ」
     */
    function backModal() {
        deletedPk.value = '';
        deletedDispFlg.value = '';
    }

    /**
     * 削除確認モーダル「はい」
     */
    async function deletedOk() {
        await deleted(deletedPk.value);
        await getCompanyManagementList();
        deletedPk.value = '';
        deletedDispFlg.value = '';
    }

    /**
     * 検索条件変更のチェックボックス切り替えイベントをトリガーにする。
     */
    async function changeSearchEvent() {
        // 1ページ目に座標を戻す。
        currentPage.value = 1;

        // 一覧を取得しなおす。
        await getCompanyManagementList();
    }
</script>

<template>
    <section id="overlay" :style="{ display: detailDispFlg }">
        <detail ref="detailComponent" @detailModal:cancel="closeDetail" @detailModal:closeAndUpdate="closeDetailAndUpdateList" />
    </section>
    <!--管理者詳細モーダル-->
    <section id="overlay" :style="{ display: userSelectDispFlg }">
        <userSelect ref="userSelectComponent" @userSelectModal:close="userSelectClose" @userSelectModal:select="userSelected" />
    </section>
    <!--ユーザー選択モーダル-->
    <section id="overlay" :style="{ display: deletedDispFlg }">
        <deleteModal ref="deletedComponent" @deletedModal:close="backModal" @deletedModal:delete="deletedOk" />
    </section>
    <!--削除確認モーダル-->
    <MainHeader />
    <div id="contents" class="headerSingleBlock">
        <div id="rightBlock" class="noPaddingTop">
            <div class="whiteBox wide">
                <h3 class="textCenter">
                    <span>{{ $t('CompanyManagementList.parts.pageTitle1') }}</span>
                </h3>
                <section class="whiteBoxContents">
                    <ul class="btnArea flexLeft">
                        <li style="padding-right: 10px">
                            <button class="btnPrimary openModalAdministratorListAdministratorDetail" @click="openDetail()">{{ $t('CompanyManagementList.parts.button1') }}</button>
                        </li>
                        <li>
                            <button class="btnNormal" id="openModalUserSelection" @click="companyManagementUserSelectOpen()">{{ $t('CompanyManagementList.parts.button2') }}</button>
                        </li>
                        <li style="padding-right: 15px; margin-left: auto">
                            <!--サービス管理者-->
                            <input type="checkbox" class="checkbox" id="checkbox-1-1" v-model="companyManagementListRequest.MngServiceAdminFlag" @change="changeSearchEvent" />
                            <label class="checkbox label" for="checkbox-1-1">{{ $t('CompanyManagementList.parts.dl1') }}</label>
                        </li>
                        <li style="padding-right: 15px">
                            <!--システム管理者-->
                            <input type="checkbox" class="checkbox" id="checkbox-1-2" v-model="companyManagementListRequest.MngSystemAdminFlag" @change="changeSearchEvent" />
                            <label class="checkbox label" for="checkbox-1-2">{{ $t('CompanyManagementList.parts.dl2') }}</label>
                        </li>
                        <li style="padding-right: 8px">
                            <!--産業保健スタッフ-->
                            <input type="checkbox" class="checkbox" id="checkbox-1-3" v-model="companyManagementListRequest.MngHealthStaffFlag" @change="changeSearchEvent" />
                            <label class="checkbox label" for="checkbox-1-3">{{ $t('CompanyManagementList.parts.dl3') }}</label>
                        </li>
                    </ul>
                    <article class="innerTable">
                        <table class="table noSelection" id="sample">
                            <thead>
                                <tr>
                                    <th>
                                        <span>{{ $t('CompanyManagementList.parts.tableHeader1') }}</span>
                                    </th>
                                    <th>
                                        <span>{{ $t('CompanyManagementList.parts.tableHeader2') }}</span>
                                    </th>
                                    <th>
                                        <span>{{ $t('CompanyManagementList.parts.tableHeader3') }}</span>
                                    </th>
                                    <th>
                                        <span>{{ $t('CompanyManagementList.parts.tableHeader4') }}</span>
                                    </th>
                                    <th>
                                        <span>{{ $t('CompanyManagementList.parts.tableHeader5') }}</span>
                                    </th>
                                    <th>
                                        <span>{{ $t('CompanyManagementList.parts.tableHeader6') }}</span>
                                    </th>
                                    <th>
                                        <span>{{ $t('CompanyManagementList.parts.tableHeader7') }}</span>
                                    </th>
                                    <th>
                                        <span>{{ $t('CompanyManagementList.parts.tableHeader8') }}</span>
                                    </th>
                                    <th style="width: 20px; margin-left: auto"><span>編集</span></th>
                                    <th style="width: 20px"><span>削除</span></th>
                                </tr>
                            </thead>
                            <tbody v-for="list in companyManagementListStore.mngList" :key="list.index">
                                <tr>
                                    <td>
                                        <!--ユーザーID-->
                                        <span>{{ list.UserId }}</span>
                                    </td>
                                    <td>
                                        <!--メールアドレス-->
                                        <span>{{ list.MailAddress }}</span>
                                    </td>
                                    <td>
                                        <!--氏名-->
                                        <span>{{ list.UserNm }}</span>
                                    </td>
                                    <td>
                                        <!--利用者登録-->
                                        <span>
                                            <div v-if="list.CliFlag">{{ $t('CompanyManagementList.parts.dl6') }}</div>
                                            <div v-else>{{ $t('CompanyManagementList.parts.dl7') }}</div>
                                        </span>
                                    </td>
                                    <td>
                                        <!--利用停止-->
                                        <span>
                                            <div v-if="list.SuspendFlag">{{ $t('CompanyManagementList.parts.dl8') }}</div>
                                            <div v-else />
                                        </span>
                                    </td>
                                    <td>
                                        <!--サービス管理者-->
                                        <span>
                                            <div v-if="list.MngServiceAdminFlag">{{ $t('CompanyManagementList.parts.dl8') }}</div>
                                            <div v-else />
                                        </span>
                                    </td>
                                    <td>
                                        <!--システム管理者-->
                                        <span>
                                            <div v-if="list.MngSystemAdminFlag">{{ $t('CompanyManagementList.parts.dl8') }}</div>
                                            <div v-else />
                                        </span>
                                    </td>
                                    <td>
                                        <!--産業保健スタッフ-->
                                        <span>
                                            <div v-if="list.MngHealthStaffFlag">{{ $t('CompanyManagementList.parts.dl8') }}</div>
                                            <div v-else />
                                        </span>
                                    </td>
                                    <td>
                                        <!--編集-->
                                        <button class="btnEdit" id="openModalMngListMngDetailUpdate" @click="openDetail(list.UserPk)">{{ $t('CompanyManagementList.parts.button7') }}</button>
                                    </td>
                                    <td>
                                        <!--削除-->
                                        <button class="btnDelete" id="openModalMngListMngDetailDelete" @click="deletedModal(list.UserPk)">{{ $t('CompanyManagementList.parts.button8') }}</button>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </article>
                    <Pagination ref="pagination" :currentPage="currentPage" :totalPages="totalPages" @pageChanged="handlePageChange" />
                </section>
                <section class="whiteBoxFooter">
                    <ul class="btnArea center">
                        <li>
                            <RouterLink :to="{ name: 'home' }">
                                <button type="button" id="btnBack" class="btnNormal">{{ $t('CompanyManagementList.parts.button9') }}</button>
                            </RouterLink>
                        </li>
                    </ul>
                </section>
            </div>
        </div>
    </div>
</template>

<style></style>
